// Contrato de ingesta de datos de máquina (propuesta 2026-10-alimentos-rionegro).
// Lo consumen el API (`/api/ingesta/*`), la fábrica simulada y cualquier
// integrador de planta. Diseño completo en
// docs/propuestas/2026-10-alimentos-rionegro/contrato-ingesta.md.
//
// Regla: todo evento lleva id único por tenant y línea, secuencia monótona por
// línea y hora de origen con zona. El servidor nunca confía en el cuerpo para
// identificar al tenant: lo deriva de la credencial.
import { z } from 'zod';
export const INGESTA_MAX_EVENTOS_POR_PETICION = 200;
/**
 * Tope de contadores acumulados (unidades y kg). Cabe en NUMERIC(16,3) de
 * `ingesta_estado_linea`; un valor mayor se rechaza por evento (S1, B8).
 */
export const INGESTA_MAX_CONTADOR = 9.99e12;
const codigoExterno = z.string().trim().min(1).max(64);
const idEvento = z.string().trim().min(1).max(64);
const refParo = z.string().trim().min(1).max(64);
// El formato ISO no basta: un desfase como +99:00 pasa la expresión regular
// pero no es una fecha (S1).
const horaOrigen = z
    .string()
    .datetime({ offset: true })
    .refine((s) => Number.isFinite(Date.parse(s)), { message: 'hora_origen no es una fecha válida' });
const contador = z.number().finite().min(0).max(INGESTA_MAX_CONTADOR);
export const fuenteCapturaLineaSchema = z.enum(['manual', 'hibrida', 'maquina']);
export const estadoMaquinaSchema = z.enum(['produciendo', 'parada', 'cambio', 'sin_programa']);
export const conectividadLineaSchema = z.enum(['en_linea', 'sin_senal', 'recuperando']);
// --- Eventos -----------------------------------------------------------------
const eventoBase = {
    id: idEvento,
    linea: codigoExterno,
    secuencia: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    hora_origen: horaOrigen,
};
export const eventoLatidoSchema = z.object({
    ...eventoBase,
    tipo: z.literal('latido'),
    datos: z.object({ estado_maquina: estadoMaquinaSchema.optional() }).strict().default({}),
});
export const eventoConteoSchema = z.object({
    ...eventoBase,
    tipo: z.literal('conteo'),
    datos: z
        .object({
        unidades_acumuladas: contador,
        kg_acumulados: contador.optional(),
    })
        .strict(),
});
export const eventoSkuActivoSchema = z.object({
    ...eventoBase,
    tipo: z.literal('sku_activo'),
    datos: z
        .object({
        sku: codigoExterno,
        lote: z.string().trim().min(1).max(32).optional(),
    })
        .strict(),
});
export const eventoParoAbiertoSchema = z.object({
    ...eventoBase,
    tipo: z.literal('paro_abierto'),
    datos: z
        .object({
        paro_ref: refParo,
        causal: codigoExterno.optional(),
        observaciones: z.string().trim().max(500).optional(),
    })
        .strict(),
});
export const eventoParoCerradoSchema = z.object({
    ...eventoBase,
    tipo: z.literal('paro_cerrado'),
    datos: z
        .object({
        paro_ref: refParo,
        causal: codigoExterno.optional(),
    })
        .strict(),
});
export const eventoLoteSchema = z.object({
    ...eventoBase,
    tipo: z.literal('lote'),
    datos: z.object({ lote: z.string().trim().min(1).max(32) }).strict(),
});
export const eventoMaquinaSchema = z.discriminatedUnion('tipo', [
    eventoLatidoSchema,
    eventoConteoSchema,
    eventoSkuActivoSchema,
    eventoParoAbiertoSchema,
    eventoParoCerradoSchema,
    eventoLoteSchema,
]);
export const ingestaEventosRequestSchema = z.object({
    eventos: z.array(eventoMaquinaSchema).min(1).max(INGESTA_MAX_EVENTOS_POR_PETICION),
});
// --- Respuesta ---------------------------------------------------------------
export const resultadoEventoEstadoSchema = z.enum([
    'aceptado',
    'duplicado',
    'rechazado',
    'en_cola',
]);
export const razonIngestaSchema = z.enum([
    'linea_desconocida',
    'linea_manual',
    'sku_desconocido',
    'causal_desconocida',
    'reloj_futuro',
    'reloj_incoherente',
    'fuera_de_ventana',
    'paro_ref_desconocida',
    'datos_invalidos',
    'sin_turno',
    'sin_sku_activo',
    'solape_manual',
    'conflicto_humano',
    'desorden_no_resoluble',
]);
export const resultadoEventoSchema = z.object({
    id: idEvento,
    estado: resultadoEventoEstadoSchema,
    razon: razonIngestaSchema.optional(),
    detalle: z.string().optional(),
});
export const estadoLineaIngestaSchema = z.object({
    linea: codigoExterno,
    linea_id: z.string().uuid(),
    nombre: z.string(),
    fuente_captura: fuenteCapturaLineaSchema,
    conectividad: conectividadLineaSchema,
    ultimo_latido_at: z.string().datetime({ offset: true }).nullable(),
    ultimo_evento_at: z.string().datetime({ offset: true }).nullable(),
    estado_maquina: estadoMaquinaSchema.nullable(),
    sku_actual: codigoExterno.nullable(),
    lote_actual: z.string().nullable(),
    registro_actual_id: z.string().uuid().nullable(),
    paro_abierto_ref: refParo.nullable(),
    contador_ultimo_unidades: z.number().nullable(),
    contador_ultimo_kg: z.number().nullable(),
    // Producción del TURNO según JIDOKAP (solo en GET /api/ingesta/estado).
    // No es el contador de la máquina: suma las filas válidas de la línea en la
    // instancia de turno en curso (misma resolución que la pantalla). null si la
    // planta está entre turnos.
    producido_turno: z.number().nullable().optional(),
    turno_codigo: z.string().nullable().optional(),
    turno_nombre: z.string().nullable().optional(),
    /** Cantidad de la fila de la hora en curso (`registro_actual_id`). */
    registro_actual_cantidad: z.number().nullable().optional(),
});
export const ingestaEventosResponseSchema = z.object({
    resultados: z.array(resultadoEventoSchema),
    lineas: z.array(estadoLineaIngestaSchema),
});
export const ingestaEstadoResponseSchema = z.object({
    lineas: z.array(estadoLineaIngestaSchema),
    servidor_at: z.string().datetime({ offset: true }),
});
// --- Configuración (Admin) ---------------------------------------------------
export const tipoMapeoIngestaSchema = z.enum(['linea', 'sku', 'causal']);
export const mapeoIngestaSchema = z.object({
    id: z.string().uuid(),
    tipo: tipoMapeoIngestaSchema,
    codigo_externo: codigoExterno,
    destino_id: z.string().uuid(),
    destino_nombre: z.string(),
});
export const mapeoIngestaCreateSchema = mapeoIngestaSchema.pick({
    tipo: true,
    codigo_externo: true,
    destino_id: true,
});
export const credencialIngestaSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string().min(1).max(80),
    prefijo: z.string().length(8),
    activa: z.boolean(),
    created_at: z.string(),
    ultimo_uso_at: z.string().nullable(),
    revocada_at: z.string().nullable(),
});
export const credencialIngestaCreateSchema = z.object({ nombre: z.string().trim().min(1).max(80) });
// El secreto completo solo viaja en la respuesta de creación.
export const credencialIngestaCreadaSchema = credencialIngestaSchema.extend({
    secreto: z.string().min(20),
});
export const ingestaConfigSchema = z.object({
    usuario_tecnico_id: z.string().uuid().nullable(),
    causal_pendiente_id: z.string().uuid().nullable(),
    ventana_latido_seg: z.number().int().min(10).max(3600),
    ventana_gracia_min: z.number().int().min(1).max(1440),
    tolerancia_reloj_seg: z.number().int().min(0).max(3600),
});
export const ingestaConfigUpdateSchema = ingestaConfigSchema.partial();
export const lineaFuenteCapturaUpdateSchema = z.object({
    fuente_captura: fuenteCapturaLineaSchema,
    minutos_cambio_referencia_estandar: z.number().int().min(0).max(1440).nullable().optional(),
});
// Extensiones de Admin (fase A). Aditivas: no cambian ningún schema anterior.
export const skuPesoUpdateSchema = z
    .object({
    peso_nominal_g: z.number().positive().max(1_000_000).nullable().optional(),
    tolerancia_g: z.number().min(0).max(1_000_000).nullable().optional(),
    costo_referencia_kg: z.number().min(0).max(1_000_000_000).nullable().optional(),
})
    .strict()
    .refine((o) => Object.keys(o).length > 0, { message: 'Debe enviar al menos un campo' });
export const categoriaParoCambioReferenciaUpdateSchema = z
    .object({ es_cambio_referencia: z.boolean() })
    .strict();
/**
 * GET /api/admin/ingesta/resumen: valores vigentes que editan los formularios
 * de Admin (fuente de captura y estándar de cambio por línea, peso y costo por
 * SKU, marca de cambio de referencia por categoría). Solo elementos activos.
 */
export const adminIngestaResumenSchema = z.object({
    lineas: z.array(z.object({
        id: z.string().uuid(),
        codigo: z.string(),
        nombre: z.string(),
        planta_id: z.string().uuid(),
        fuente_captura: fuenteCapturaLineaSchema,
        minutos_cambio_referencia_estandar: z.number().int().nullable(),
        /** Algún SKU de la línea exige golpes por minuto (impide pasarla a máquina). */
        golpes_min_aplica_activo: z.boolean(),
    })),
    skus: z.array(z.object({
        id: z.string().uuid(),
        codigo_cliente: z.string(),
        descripcion: z.string(),
        cliente_id: z.string().uuid(),
        peso_nominal_g: z.number().nullable(),
        tolerancia_g: z.number().nullable(),
        costo_referencia_kg: z.number().nullable(),
    })),
    categorias_paro: z.array(z.object({
        id: z.string().uuid(),
        codigo: z.string(),
        nombre: z.string(),
        es_cambio_referencia: z.boolean(),
    })),
});
export const usuarioTecnicoIngestaSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    email: z.string(),
    creado: z.boolean(),
});
// --- Conectividad en lecturas existentes (campos opcionales) ------------------
export const conectividadLecturaSchema = z.object({
    conectividad: conectividadLineaSchema.nullable().optional(),
    ultimo_latido_at: z.string().nullable().optional(),
});
export const conectividadLineaItemSchema = z.object({
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    planta_id: z.string().uuid(),
    fuente_captura: fuenteCapturaLineaSchema,
    conectividad: conectividadLineaSchema.nullable(),
    ultimo_latido_at: z.string().nullable(),
    ultimo_evento_at: z.string().nullable(),
    estado_maquina: estadoMaquinaSchema.nullable(),
    ventana_sin_senal_abierta_desde: z.string().nullable(),
});
/** Respuesta de GET /api/coordinador/conectividad: líneas híbridas o de máquina de sus plantas. */
export const coordinadorConectividadResponseSchema = z.array(conectividadLineaItemSchema);
/**
 * GET /api/coordinador/conectividad/ventanas: ventanas sin señal de los últimos
 * 7 días de las líneas de sus plantas, la más reciente primero. `fin` nulo =
 * sigue abierta; `minutos` se mide hasta ahora mientras está abierta.
 */
export const ventanaSinSenalItemSchema = z.object({
    id: z.string().uuid(),
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    inicio: z.string(),
    fin: z.string().nullable(),
    minutos: z.number().int().nonnegative(),
});
export const coordinadorVentanasSinSenalResponseSchema = z.array(ventanaSinSenalItemSchema);
// --- Completar desde la tablet ------------------------------------------------
export const paroCompletarCausalSchema = z.object({
    causal_madre_id: z.string().uuid(),
    causal_id: z.string().uuid(),
    observaciones: z.string().trim().max(500).optional(),
});
export const paroPendienteCausalSchema = z.object({
    id: z.string().uuid(),
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    planta_id: z.string().uuid(),
    registro_produccion_hora_id: z.string().uuid(),
    fecha: z.string(),
    hora_inicio: z.string(),
    hora_fin: z.string().nullable(),
    minutos: z.number().nullable(),
    ref_externa: z.string().nullable(),
    observaciones: z.string().nullable(),
});
export const registroCompletarSchema = z.object({
    op_lote_oc_cliente: z.string().trim().max(32).optional(),
    numero_operarios: z.number().int().min(1).max(60).optional(),
    confirmar: z.literal(true),
});
export const registroPendienteCompletarSchema = z.object({
    id: z.string().uuid(),
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    planta_id: z.string().uuid(),
    fecha: z.string(),
    turno_id: z.string().uuid(),
    hora_inicio_bloque: z.string(),
    hora_fin_bloque: z.string(),
    sku_id: z.string().uuid(),
    sku_descripcion: z.string(),
    cantidad_procesada: z.number(),
    cantidad_kg: z.number().nullable(),
    numero_operarios: z.number(),
    op_lote_oc_cliente: z.string().nullable(),
    paros_pendientes_causal: z.number().int(),
});
// --- Sobrellenado y cambios de referencia (lecturas del coordinador) ----------
// Una fila por línea × fecha × hora × SKU (registros válidos). Con signo:
// sobrellenado negativo = bajo peso. El costo solo existe si el SKU declara
// costo_referencia_kg y solo valora el exceso (max(sobrellenado_kg, 0)).
// sin_dato_peso = la hora no trae cantidad_kg (o no tuvo unidades).
export const sobrellenadoFilaSchema = z.object({
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    fecha: z.string(),
    hora_inicio_bloque: z.string(),
    sku_id: z.string().uuid(),
    sku_descripcion: z.string(),
    unidades: z.number(),
    kg: z.number().nullable(),
    gramos_unidad_real: z.number().nullable(),
    peso_nominal_g: z.number().nullable(),
    tolerancia_g: z.number().nullable(),
    sobrellenado_g_unidad: z.number().nullable(),
    sobrellenado_kg: z.number().nullable(),
    costo_sobrellenado_cop: z.number().nullable(),
    sin_dato_peso: z.boolean(),
});
export const sobrellenadoResumenSchema = z.object({
    unidades: z.number(),
    kg: z.number().nullable(),
    sobrellenado_kg: z.number().nullable(),
    costo_sobrellenado_cop: z.number().nullable(),
    horas_sin_dato_peso: z.number().int(),
    horas_con_dato_peso: z.number().int(),
});
export const sobrellenadoResponseSchema = z.object({
    resumen: sobrellenadoResumenSchema,
    filas: z.array(sobrellenadoFilaSchema),
});
export const cambioReferenciaPasoSchema = z.object({
    paro_id: z.string().uuid(),
    causal_nombre: z.string(),
    hora_inicio: z.string(),
    hora_fin: z.string().nullable(),
    minutos: z.number().nullable(),
});
export const cambioReferenciaEpisodioSchema = z.object({
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    fecha: z.string(),
    inicio: z.string(),
    fin: z.string().nullable(),
    minutos_reales: z.number().nullable(),
    minutos_estandar: z.number().nullable(),
    diferencia_minutos: z.number().nullable(),
    sku_saliente: z.string().nullable(),
    sku_entrante: z.string().nullable(),
    pasos: z.array(cambioReferenciaPasoSchema),
});
export const cambiosReferenciaResponseSchema = z.object({
    episodios: z.array(cambioReferenciaEpisodioSchema),
});
// --- Consultas de las lecturas del coordinador (fase B) -----------------------
/** Rango máximo de las lecturas de sobrellenado y cambios de referencia. */
export const INGESTA_RANGO_MAX_DIAS = 92;
function diasEntre(desde, hasta) {
    return (Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000;
}
const rangoValido = (q) => {
    const dias = diasEntre(q.desde, q.hasta);
    return dias >= 0 && dias <= INGESTA_RANGO_MAX_DIAS;
};
const mensajeRango = {
    message: `desde debe ser <= hasta y el rango no puede superar ${INGESTA_RANGO_MAX_DIAS} días`,
};
export const sobrellenadoQuerySchema = z
    .object({
    desde: z.string().date(),
    hasta: z.string().date(),
    planta_id: z.string().uuid().optional(),
    linea_id: z.string().uuid().optional(),
})
    .refine(rangoValido, mensajeRango);
export const cambiosReferenciaQuerySchema = z
    .object({
    desde: z.string().date(),
    hasta: z.string().date(),
    linea_id: z.string().uuid().optional(),
})
    .refine(rangoValido, mensajeRango);
// --- Conflictos de ingesta para el coordinador --------------------------------
export const estadoConflictoIngestaSchema = z.enum(['pendiente', 'resuelto', 'descartado']);
/** `resueltos` agrupa `resuelto` (se aplicó la máquina) y `descartado` (se mantuvo la persona). */
export const conflictosIngestaQuerySchema = z.object({
    estado: z.enum(['pendiente', 'resueltos']).default('pendiente'),
});
export const accionConflictoIngestaSchema = z.enum(['mantener_persona', 'aplicar_maquina']);
export const conflictoIngestaSchema = z.object({
    id: z.string().uuid(),
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    planta_id: z.string().uuid(),
    registro_id: z.string().uuid().nullable(),
    paro_id: z.string().uuid().nullable(),
    /** Fecha operativa y hora del registro afectado (null si el conflicto no tiene registro). */
    fecha: z.string().nullable(),
    hora_inicio_bloque: z.string().nullable(),
    evento_id_origen: z.string(),
    hora_origen: z.string(),
    razon: z.string(),
    campo: z.string().nullable(),
    valor_actual: z.unknown(),
    valor_maquina: z.unknown(),
    estado: estadoConflictoIngestaSchema,
    /** Derivada del estado: `resuelto` = aplicar_maquina, `descartado` = mantener_persona. */
    accion: accionConflictoIngestaSchema.nullable(),
    /** Si `aplicar_maquina` está disponible para este conflicto. */
    aplicable: z.boolean(),
    created_at: z.string(),
    resuelto_at: z.string().nullable(),
    resuelto_por_user_id: z.string().uuid().nullable(),
});
export const conflictosIngestaResponseSchema = z.object({
    conflictos: z.array(conflictoIngestaSchema),
});
export const conflictoResolverSchema = z
    .object({
    accion: accionConflictoIngestaSchema,
    razon: z.string().trim().min(1).max(500).optional(),
})
    .strict();
//# sourceMappingURL=ingesta_maquina.js.map